import { useCallback, useEffect, useMemo, useState } from 'react';
import { BuildingSummary, GroupTag } from '../types/protocols';
import {
    getBuildingTags,
    addBuildingTag,
    updateBuildingTag,
    deleteBuildingTag,
} from '../api/buildingTagsApi';
import { useBuildingsData } from '../Helpers/BuildingSummariesContext';

/**
 * Manages building group tag definitions for the admin settings page.
 *
 * Owns the tag list (load/mutate/refresh) and derives, from the building
 * summaries, how many buildings currently use each tag so the UI can warn
 * before a delete.
 */
export const useBuildingTags = () => {
    const [tags, setTags] = useState<GroupTag[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    const { buildings, refreshBuildings } = useBuildingsData();

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            setTags(await getBuildingTags());
        } catch (err) {
            console.error('Failed to load building tags', err);
            setError('Kunde inte ladda byggnadsgrupper.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    // tagKey -> buildings assigned to it, sorted by name
    const buildingsByTag = useMemo(() => {
        const map: Record<string, BuildingSummary[]> = {};
        (buildings ?? []).forEach((b) => {
            Object.keys(b.groupTags ?? {}).forEach((tagKey) => {
                (map[tagKey] ??= []).push(b);
            });
        });
        Object.values(map).forEach((list) =>
            list.sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '', 'sv'))
        );
        return map;
    }, [buildings]);

    // tagKey -> number of buildings assigned to it
    const usageByTag = useMemo(() => {
        const counts: Record<string, number> = {};
        Object.entries(buildingsByTag).forEach(([tagKey, list]) => {
            counts[tagKey] = list.length;
        });
        return counts;
    }, [buildingsByTag]);

    const addTag = useCallback(async (tag: string) => {
        setError(null);
        try {
            await addBuildingTag(tag);
            await refresh();
        } catch (err) {
            console.error('Failed to add building tag', err);
            setError('Kunde inte lägga till byggnadsgruppen.');
            throw err;
        }
    }, [refresh]);

    const updateTag = useCallback(async (key: string, tag: string) => {
        setError(null);
        try {
            await updateBuildingTag(key, tag);
            await refresh();
        } catch (err) {
            console.error('Failed to update building tag', err);
            setError('Kunde inte uppdatera byggnadsgruppen.');
            throw err;
        }
    }, [refresh]);

    const deleteTag = useCallback(async (key: string) => {
        setError(null);
        try {
            const buildingKeys = (buildingsByTag[key] ?? []).map((b) => b.key);
            await deleteBuildingTag(key, buildingKeys);
            await Promise.all([refresh(), refreshBuildings()]);
        } catch (err) {
            console.error('Failed to delete building tag', err);
            setError('Kunde inte ta bort byggnadsgruppen.');
            throw err;
        }
    }, [refresh, refreshBuildings, buildingsByTag]);

    return {
        tags,
        loading,
        error,
        setError,
        usageByTag,
        buildingsByTag,
        addTag,
        updateTag,
        deleteTag,
        refresh,
    };
};
